import { Trans, useLingui } from "@lingui/react/macro";
import { useParams } from "react-router";
import {
  type informationFieldConfigValueSchema,
  isPatientInformationFieldVisible,
  usePatient,
  usePatientInformationFields,
  usePatientTypes,
  useTeams,
  useEconomicUnits,
} from "@/api/Patients";
import AddressComponent from "@/components/Address/Address";
import ErrorMessage from "@medoma/components/ErrorMessage/ErrorMessage";
import { Loading } from "@medoma/components/Loading/Loading";
import styles from "./Information.module.scss";
import type { IPlainButton } from "@medoma/components/Button/Button";
import { PlainButton } from "@medoma/components/Button/Button";
import { useState } from "react";
import { EditPhoneNumber } from "@/forms/EditPhoneNumber";
import { EditAddress } from "@/forms/EditAddress";
import { EditInformation } from "@/forms/EditInformation";
import { DescriptionList } from "@/components/DescriptionList/DescriptionList";
import { EditRelatives } from "@/forms/EditRelatives";
import { deducedError } from "@/Utils/ErrorUtils";
import { EditHealthcareJourneyNumber } from "@/forms/EditHealthcareJourneyNumber";
import { EditSafetyAlarmText } from "@/forms/EditSafetyAlarm";
import { z } from "zod";
import { EditName } from "@/forms/EditName";
import { dateName } from "@/Utils/DateUtils";
import { EditWard } from "@/forms/EditWard";
import { knownFeatureFlagsSchema, useFeatureFlag } from "@/api/FeatureFlags";
import * as Sentry from "@sentry/react";
import {
  getPatientFullName,
  patientStatusSchema,
  patientTypeDictionary,
  type IExistingPatient,
  type IPatientType,
} from "@medoma/models/patients";
import { EditArea } from "@/forms/EditArea";
import { EditTeam } from "@/forms/EditTeam";
import { EditEconomicUnit } from "@/forms/EditEconomicUnit";
import { EditPets } from "@/forms/EditPets";
import { EditFlags } from "@/forms/EditFlags";
import { EditCareSummary } from "@/forms/EditCareSummary";
import { EditHomeCare } from "@/forms/EditHomeCare";
import { EditLifeCare } from "@/forms/EditLifeCare";
import { Text, TextWithLineBreaks } from "@medoma/components/Text/Text";
import { EditDiagnosisCode } from "@/forms/EditDiagnosisCode";
import { localizePhoneNumber } from "@medoma/models/phoneNumbers";
import { useUserProfile } from "@/api/Users";
import { EditExternalCareEpisodeId } from "@/forms/EditExternalCareEpisodeId";
import { EditPlannedAdmissionDateTime } from "@/forms/EditPlannedAdmissionDateTime";
import { EditPlannedDischargeDateTime } from "@/forms/EditPlannedDischargeDateTime";
import { EditPostDischargeProcessed } from "@/forms/EditPostDischargeProcessed";
import { EditPrimaryResponsible } from "@/forms/EditPrimaryResponsible";
import { EditReferringUnit } from "@/forms/EditReferringUnit";
import { EditPlaceOfAssessment } from "@/forms/EditPlaceOfAssessment";
import { EditPaymentExempt } from "@/forms/EditPaymentExempt";
import { EditLanguage } from "@/forms/EditLanguage";
import { EditReasonForAdmission } from "@/forms/EditReasonForAdmission";
import { useEmployee } from "@/api/Employee";
import { employeeName } from "@medoma/models/shifts";
import { EditContainer } from "./EditContainer";
import { EditAdditionalAddressInformation } from "@/forms/EditAdditionalAddressInformation";
import { EditKeyToPatient } from "@/forms/EditKeyToPatient";
import { PatientMap } from "./PatientMap";

const EditButton = ({
  children,
  onClick,
  disabled,
}: Pick<IPlainButton, "children" | "onClick" | "disabled">) => (
  <PlainButton
    size="small"
    weight="light"
    onClick={onClick}
    disabled={disabled}
  >
    {children}
  </PlainButton>
);

const editableFieldSchema = z.enum([
  "diagnosisCode",
  "address",
  "additionalAddressInformation",
  "keyToPatient",
  "phoneNumber",
  "healthcareJourneyNumber",
  "information",
  "safetyAlarmText",
  "name",
  "ward",
  "area",
  "team",
  "economicUnit",
  "pet",
  "flags",
  "careSummary",
  "homeCare",
  "lifeCare",
  "externalCareEpisodeId",
  "plannedAdmissionDateTime",
  "plannedDischargeDateTime",
  "postDischargeProcessed",
  "primaryResponsible",
  "referringUnit",
  "placeOfAssessment",
  "paymentExempt",
  "language",
  "reasonForAdmission",
  "relatives",
]);
type EditableField = z.infer<typeof editableFieldSchema>;
// Some editable fields don't map 1:1 to a patient data field:
// - `diagnosisCode` reads from `diagnosis`
// - `additionalAddressInformation` reads from `address` (address.additionalInformation)
// - `keyToPatient` reads from `address` (address.keyToPatient)
// so we exclude those editable-only names and add the real data field(s).
const editableFieldWithOwnDataFieldSchema = editableFieldSchema.exclude([
  editableFieldSchema.enum.diagnosisCode,
  editableFieldSchema.enum.additionalAddressInformation,
  editableFieldSchema.enum.keyToPatient,
]);
type DataField =
  | z.infer<typeof editableFieldWithOwnDataFieldSchema>
  | "diagnosis";

type EditableFieldParameters<
  FieldInFocus extends EditableField,
  // Used when the editable field and the field where the data is pulled from don't have identical names.
  // Defaults to `FieldInFocus`.
  DataFieldInFocus extends DataField = Exclude<
    FieldInFocus,
    typeof editableFieldSchema.enum.diagnosisCode
  > extends DataField
    ? Exclude<FieldInFocus, typeof editableFieldSchema.enum.diagnosisCode>
    : never,
> = {
  currentlyEditing: EditableField | null;
  setCurrentlyEditing: (nowEditing: EditableField | null) => void;
  patient: Pick<IExistingPatient, "id" | DataFieldInFocus>;
};

const NoDescription = () => {
  const { t } = useLingui();
  return (
    <Text element="span" color="no-results" aria-label={t`Inget angivet`}>
      -
    </Text>
  );
};

const EditControl = ({
  field,
  currentlyEditing,
  setCurrentlyEditing,
}: {
  field: EditableField;
  currentlyEditing: EditableField | null;
  setCurrentlyEditing: (nowEditing: EditableField | null) => void;
}) => {
  return (
    <div className={styles.editButton}>
      {currentlyEditing !== field ? (
        <EditButton onClick={() => setCurrentlyEditing(field)}>
          <Trans>Ändra</Trans>
        </EditButton>
      ) : (
        <EditButton onClick={() => setCurrentlyEditing(null)}>
          <Trans>Avbryt</Trans>
        </EditButton>
      )}
    </div>
  );
};

const Name = ({
  currentlyEditing,
  setCurrentlyEditing,
  patient,
}: EditableFieldParameters<"name">) => {
  return (
    <>
      <DescriptionList.Term>
        <Trans>Namn</Trans>
      </DescriptionList.Term>

      <DescriptionList.Description>
        <div className={styles.description}>
          <div className={styles.descriptionContent}>
            {getPatientFullName(patient.name)}

            {currentlyEditing === editableFieldSchema.enum.name ? (
              <EditContainer>
                <EditName
                  currentName={patient.name}
                  onSuccess={() => setCurrentlyEditing(null)}
                  patientId={patient.id}
                />
              </EditContainer>
            ) : null}
          </div>

          <EditControl
            field={editableFieldSchema.enum.name}
            currentlyEditing={currentlyEditing}
            setCurrentlyEditing={setCurrentlyEditing}
          />
        </div>
      </DescriptionList.Description>
    </>
  );
};

const DiagnosisCode = ({
  currentlyEditing,
  setCurrentlyEditing,
  patient,
  fieldConfig,
}: EditableFieldParameters<"diagnosisCode", "diagnosis"> & {
  fieldConfig: z.infer<typeof informationFieldConfigValueSchema>;
}) => {
  return (
    <>
      <DescriptionList.Term>
        <Trans>Diagnoskod</Trans>
      </DescriptionList.Term>

      <DescriptionList.Description>
        <div className={styles.description}>
          <div className={styles.descriptionContent}>
            {patient.diagnosis ? (
              patient.diagnosis.title ? (
                // Happy path
                `${patient.diagnosis.code} - ${patient.diagnosis.title}` // Data created before diagnosis title was added, missing title
              ) : (
                patient.diagnosis.code
              )
            ) : (
              // No diagnosis
              <NoDescription />
            )}

            {currentlyEditing === editableFieldSchema.enum.diagnosisCode ? (
              <EditContainer>
                <EditDiagnosisCode
                  currentDiagnosisCode={patient.diagnosis?.code}
                  onSuccess={() => setCurrentlyEditing(null)}
                  patientId={patient.id}
                  fieldConfig={fieldConfig}
                />
              </EditContainer>
            ) : null}
          </div>

          <EditControl
            field={editableFieldSchema.enum.diagnosisCode}
            currentlyEditing={currentlyEditing}
            setCurrentlyEditing={setCurrentlyEditing}
          />
        </div>
      </DescriptionList.Description>
    </>
  );
};

const Address = ({
  currentlyEditing,
  setCurrentlyEditing,
  patient,
}: EditableFieldParameters<"address">) => {
  const { countryCode } = useUserProfile();
  return (
    <>
      <DescriptionList.Term>
        <Trans>Adress</Trans>
      </DescriptionList.Term>

      <DescriptionList.Description>
        <div className={styles.description}>
          <div className={styles.descriptionContent}>
            <AddressComponent
              address={{
                ...patient.address,
                countryCode,
              }}
              showPostalCode
            />

            {currentlyEditing === editableFieldSchema.enum.address ? (
              <EditContainer>
                <EditAddress
                  currentAddress={patient.address}
                  onSuccess={() => setCurrentlyEditing(null)}
                  patientId={patient.id}
                />
              </EditContainer>
            ) : null}
          </div>

          <EditControl
            field={editableFieldSchema.enum.address}
            currentlyEditing={currentlyEditing}
            setCurrentlyEditing={setCurrentlyEditing}
          />
        </div>
      </DescriptionList.Description>
    </>
  );
};

const AdditionalAddressInformation = ({
  currentlyEditing,
  setCurrentlyEditing,
  patient,
  fieldConfig,
}: EditableFieldParameters<"additionalAddressInformation", "address"> & {
  fieldConfig: z.infer<typeof informationFieldConfigValueSchema>;
}) => {
  return (
    <>
      <DescriptionList.Term>
        <Trans>Övrig adressinformation</Trans>
      </DescriptionList.Term>

      <DescriptionList.Description>
        <div className={styles.description}>
          <div className={styles.descriptionContent}>
            {patient.address.additionalInformation ? (
              <TextWithLineBreaks>
                {patient.address.additionalInformation}
              </TextWithLineBreaks>
            ) : (
              <NoDescription />
            )}

            {currentlyEditing ===
            editableFieldSchema.enum.additionalAddressInformation ? (
              <EditContainer>
                <EditAdditionalAddressInformation
                  currentAddress={patient.address}
                  onSuccess={() => setCurrentlyEditing(null)}
                  patientId={patient.id}
                  fieldConfig={fieldConfig}
                />
              </EditContainer>
            ) : null}
          </div>

          <EditControl
            field={editableFieldSchema.enum.additionalAddressInformation}
            currentlyEditing={currentlyEditing}
            setCurrentlyEditing={setCurrentlyEditing}
          />
        </div>
      </DescriptionList.Description>
    </>
  );
};

const Key = ({
  currentlyEditing,
  setCurrentlyEditing,
  patient,
  fieldConfig,
}: EditableFieldParameters<"keyToPatient", "address"> & {
  fieldConfig: z.infer<typeof informationFieldConfigValueSchema>;
}) => {
  return (
    <>
      <DescriptionList.Term>
        <Trans>Nyckel</Trans>
      </DescriptionList.Term>

      <DescriptionList.Description>
        <div className={styles.description}>
          <div className={styles.descriptionContent}>
            {patient.address.keyToPatient ? (
              <TextWithLineBreaks>
                {patient.address.keyToPatient}
              </TextWithLineBreaks>
            ) : (
              <NoDescription />
            )}

            {currentlyEditing === editableFieldSchema.enum.keyToPatient ? (
              <EditContainer>
                <EditKeyToPatient
                  currentAddress={patient.address}
                  onSuccess={() => setCurrentlyEditing(null)}
                  patientId={patient.id}
                  fieldConfig={fieldConfig}
                />
              </EditContainer>
            ) : null}
          </div>

          <EditControl
            field={editableFieldSchema.enum.keyToPatient}
            currentlyEditing={currentlyEditing}
            setCurrentlyEditing={setCurrentlyEditing}
          />
        </div>
      </DescriptionList.Description>
    </>
  );
};

const Phone = ({
  currentlyEditing,
  setCurrentlyEditing,
  patient,
}: EditableFieldParameters<"phoneNumber">) => {
  const { countryCode } = useUserProfile();
  return (
    <>
      <DescriptionList.Term>
        <Trans>Telefonnummer</Trans>
      </DescriptionList.Term>

      <DescriptionList.Description>
        <div className={styles.description}>
          <div className={styles.descriptionContent}>
            {patient.phoneNumber ? (
              localizePhoneNumber({
                countryCode,
                phoneNumber: patient.phoneNumber,
              })
            ) : (
              <NoDescription />
            )}

            {currentlyEditing === editableFieldSchema.enum.phoneNumber ? (
              <EditContainer>
                <EditPhoneNumber
                  currentPhoneNumber={patient.phoneNumber}
                  onSuccess={() => setCurrentlyEditing(null)}
                  patientId={patient.id}
                />
              </EditContainer>
            ) : null}
          </div>

          <EditControl
            field={editableFieldSchema.enum.phoneNumber}
            currentlyEditing={currentlyEditing}
            setCurrentlyEditing={setCurrentlyEditing}
          />
        </div>
      </DescriptionList.Description>
    </>
  );
};

const GeneralInformation = ({
  currentlyEditing,
  setCurrentlyEditing,
  patient,
  fieldConfig,
}: EditableFieldParameters<"information"> & {
  fieldConfig: z.infer<typeof informationFieldConfigValueSchema>;
}) => {
  return (
    <>
      <DescriptionList.Term>
        <Trans>Allmän information</Trans>
      </DescriptionList.Term>

      <DescriptionList.Description>
        <div className={styles.description}>
          <div className={styles.descriptionContent}>
            {patient.information ? (
              <TextWithLineBreaks>{patient.information}</TextWithLineBreaks>
            ) : (
              <NoDescription />
            )}

            {currentlyEditing === editableFieldSchema.enum.information ? (
              <EditContainer>
                <EditInformation
                  currentInformation={patient.information}
                  onSuccess={() => setCurrentlyEditing(null)}
                  patientId={patient.id}
                  fieldConfig={fieldConfig}
                />
              </EditContainer>
            ) : null}
          </div>

          <EditControl
            field={editableFieldSchema.enum.information}
            currentlyEditing={currentlyEditing}
            setCurrentlyEditing={setCurrentlyEditing}
          />
        </div>
      </DescriptionList.Description>
    </>
  );
};

const Ward = ({
  currentlyEditing,
  setCurrentlyEditing,
  patient,
}: EditableFieldParameters<"ward">) => {
  return (
    <>
      <DescriptionList.Term>
        <Trans>Avdelning</Trans>
      </DescriptionList.Term>

      <DescriptionList.Description>
        <div className={styles.description}>
          <div className={styles.descriptionContent}>
            {patient.ward.displayName}

            {currentlyEditing === editableFieldSchema.enum.ward ? (
              <EditContainer>
                <EditWard
                  currentWard={patient.ward}
                  onSuccess={() => setCurrentlyEditing(null)}
                  patientId={patient.id}
                />
              </EditContainer>
            ) : null}
          </div>

          <EditControl
            field={editableFieldSchema.enum.ward}
            currentlyEditing={currentlyEditing}
            setCurrentlyEditing={setCurrentlyEditing}
          />
        </div>
      </DescriptionList.Description>
    </>
  );
};

const GeographicalArea = ({
  currentlyEditing,
  setCurrentlyEditing,
  patient,
}: EditableFieldParameters<"area">) => {
  return (
    <>
      <DescriptionList.Term>
        <Trans>Område</Trans>
      </DescriptionList.Term>

      <DescriptionList.Description>
        <div className={styles.description}>
          <div className={styles.descriptionContent}>
            {patient.area ? patient.area.displayName : <NoDescription />}

            {currentlyEditing === editableFieldSchema.enum.area ? (
              <EditContainer>
                <EditArea
                  currentArea={patient.area}
                  onSuccess={() => setCurrentlyEditing(null)}
                  patientId={patient.id}
                />
              </EditContainer>
            ) : null}
          </div>

          <EditControl
            field={editableFieldSchema.enum.area}
            currentlyEditing={currentlyEditing}
            setCurrentlyEditing={setCurrentlyEditing}
          />
        </div>
      </DescriptionList.Description>
    </>
  );
};

const Team = ({
  currentlyEditing,
  setCurrentlyEditing,
  patient,
}: EditableFieldParameters<"team">) => {
  return (
    <>
      <DescriptionList.Term>
        <Trans>Team</Trans>
      </DescriptionList.Term>

      <DescriptionList.Description>
        <div className={styles.description}>
          <div className={styles.descriptionContent}>
            {patient.team ? patient.team.displayName : <NoDescription />}

            {currentlyEditing === editableFieldSchema.enum.team ? (
              <EditContainer>
                <EditTeam
                  currentTeam={patient.team}
                  onSuccess={() => setCurrentlyEditing(null)}
                  patientId={patient.id}
                />
              </EditContainer>
            ) : null}
          </div>

          <EditControl
            field={editableFieldSchema.enum.team}
            currentlyEditing={currentlyEditing}
            setCurrentlyEditing={setCurrentlyEditing}
          />
        </div>
      </DescriptionList.Description>
    </>
  );
};

const EconomicUnit = ({
  currentlyEditing,
  setCurrentlyEditing,
  patient,
}: EditableFieldParameters<"economicUnit">) => {
  return (
    <>
      <DescriptionList.Term>
        <Trans>Ekonomisk enhet</Trans>
      </DescriptionList.Term>

      <DescriptionList.Description>
        <div className={styles.description}>
          <div className={styles.descriptionContent}>
            {patient.economicUnit ? (
              patient.economicUnit.displayName
            ) : (
              <NoDescription />
            )}

            {currentlyEditing === editableFieldSchema.enum.economicUnit ? (
              <EditContainer>
                <EditEconomicUnit
                  currentEconomicUnit={patient.economicUnit}
                  onSuccess={() => setCurrentlyEditing(null)}
                  patientId={patient.id}
                />
              </EditContainer>
            ) : null}
          </div>

          <EditControl
            field={editableFieldSchema.enum.economicUnit}
            currentlyEditing={currentlyEditing}
            setCurrentlyEditing={setCurrentlyEditing}
          />
        </div>
      </DescriptionList.Description>
    </>
  );
};

const Relatives = ({
  currentlyEditing,
  setCurrentlyEditing,
  patient,
}: {
  currentlyEditing: EditableField | null;
  setCurrentlyEditing: (nowEditing: EditableField | null) => void;
  patient: Pick<IExistingPatient, "id" | "name" | "relatives">;
}) => {
  const { countryCode } = useUserProfile();

  const relativesList = patient.relatives.map((relative) => {
    const {
      id,
      name: relativeName,
      phoneNumber: relativePhoneNumber,
      relation: relationUnknownCase,
      isLivingTogether,
    } = relative;
    const relation = relationUnknownCase.toLocaleLowerCase();
    const patientName = getPatientFullName(patient.name);

    const localizedPhoneNumber = relativePhoneNumber
      ? localizePhoneNumber({
          countryCode,
          phoneNumber: relativePhoneNumber,
        })
      : null;

    return (
      <li key={id}>
        {isLivingTogether ? (
          localizedPhoneNumber ? (
            <Trans>
              {relativeName} ({relation}) bor med {patientName} och nås på{" "}
              {localizedPhoneNumber}
            </Trans>
          ) : (
            <Trans>
              {relativeName} ({relation}) bor med {patientName} och saknar
              telefonnummer
            </Trans>
          )
        ) : localizedPhoneNumber ? (
          <Trans>
            {relativeName} ({relation}) nås på {localizedPhoneNumber}
          </Trans>
        ) : (
          <Trans>
            {relativeName} ({relation}) saknar telefonnummer
          </Trans>
        )}
      </li>
    );
  });

  return (
    <>
      <DescriptionList.Term>
        <Trans>Anhöriga</Trans>
      </DescriptionList.Term>

      <DescriptionList.Description>
        <div className={styles.description}>
          <div className={styles.descriptionContent}>
            {patient.relatives.length >= 1 ? (
              <ul className={styles.relativeList}>{relativesList}</ul>
            ) : (
              <NoDescription />
            )}

            {currentlyEditing === editableFieldSchema.enum.relatives ? (
              <EditRelatives
                patientId={patient.id}
                currentRelatives={patient.relatives}
              />
            ) : null}
          </div>

          <EditControl
            field={editableFieldSchema.enum.relatives}
            currentlyEditing={currentlyEditing}
            setCurrentlyEditing={setCurrentlyEditing}
          />
        </div>
      </DescriptionList.Description>
    </>
  );
};

const MedicalTransportNumber = ({
  currentlyEditing,
  setCurrentlyEditing,
  patient,
  fieldConfig,
}: EditableFieldParameters<"healthcareJourneyNumber"> & {
  fieldConfig: z.infer<typeof informationFieldConfigValueSchema>;
}) => {
  return (
    <>
      <DescriptionList.Term>
        <Trans>Sjukresenummer</Trans>
      </DescriptionList.Term>

      <DescriptionList.Description>
        <div className={styles.description}>
          <div className={styles.descriptionContent}>
            {patient.healthcareJourneyNumber ? (
              patient.healthcareJourneyNumber
            ) : (
              <NoDescription />
            )}

            {currentlyEditing ===
            editableFieldSchema.enum.healthcareJourneyNumber ? (
              <EditContainer>
                <EditHealthcareJourneyNumber
                  currentHealthcareJourneyNumber={
                    patient.healthcareJourneyNumber
                  }
                  onSuccess={() => setCurrentlyEditing(null)}
                  patientId={patient.id}
                  fieldConfig={fieldConfig}
                />
              </EditContainer>
            ) : null}
          </div>

          <EditControl
            field={editableFieldSchema.enum.healthcareJourneyNumber}
            currentlyEditing={currentlyEditing}
            setCurrentlyEditing={setCurrentlyEditing}
          />
        </div>
      </DescriptionList.Description>
    </>
  );
};

const SafetyAlarm = ({
  currentlyEditing,
  setCurrentlyEditing,
  patient,
  fieldConfig,
}: EditableFieldParameters<"safetyAlarmText"> & {
  fieldConfig: z.infer<typeof informationFieldConfigValueSchema>;
}) => {
  return (
    <>
      <DescriptionList.Term>
        <Trans>Sociala insatser</Trans>
      </DescriptionList.Term>

      <DescriptionList.Description>
        <div className={styles.description}>
          <div className={styles.descriptionContent}>
            {patient.safetyAlarmText ? (
              <TextWithLineBreaks>{patient.safetyAlarmText}</TextWithLineBreaks>
            ) : (
              <NoDescription />
            )}

            {currentlyEditing === editableFieldSchema.enum.safetyAlarmText ? (
              <EditContainer>
                <EditSafetyAlarmText
                  currentSafetyAlarmText={patient.safetyAlarmText}
                  onSuccess={() => setCurrentlyEditing(null)}
                  patientId={patient.id}
                  fieldConfig={fieldConfig}
                />
              </EditContainer>
            ) : null}
          </div>

          <EditControl
            field={editableFieldSchema.enum.safetyAlarmText}
            currentlyEditing={currentlyEditing}
            setCurrentlyEditing={setCurrentlyEditing}
          />
        </div>
      </DescriptionList.Description>
    </>
  );
};

const Pet = ({
  currentlyEditing,
  setCurrentlyEditing,
  patient,
  fieldConfig,
}: EditableFieldParameters<"pet"> & {
  fieldConfig: z.infer<typeof informationFieldConfigValueSchema>;
}) => {
  return (
    <>
      <DescriptionList.Term>
        <Trans>Husdjur</Trans>
      </DescriptionList.Term>

      <DescriptionList.Description>
        <div className={styles.description}>
          <div className={styles.descriptionContent}>
            {patient.pet ? (
              <TextWithLineBreaks>{patient.pet}</TextWithLineBreaks>
            ) : (
              <NoDescription />
            )}

            {currentlyEditing === editableFieldSchema.enum.pet ? (
              <EditContainer>
                <EditPets
                  currentPets={patient.pet}
                  onSuccess={() => setCurrentlyEditing(null)}
                  patientId={patient.id}
                  fieldConfig={fieldConfig}
                />
              </EditContainer>
            ) : null}
          </div>

          <EditControl
            field={editableFieldSchema.enum.pet}
            currentlyEditing={currentlyEditing}
            setCurrentlyEditing={setCurrentlyEditing}
          />
        </div>
      </DescriptionList.Description>
    </>
  );
};

const Language = ({
  currentlyEditing,
  setCurrentlyEditing,
  patient,
  fieldConfig,
}: EditableFieldParameters<"language"> & {
  fieldConfig: z.infer<typeof informationFieldConfigValueSchema>;
}) => {
  return (
    <>
      <DescriptionList.Term>
        <Trans>Språk</Trans>
      </DescriptionList.Term>

      <DescriptionList.Description>
        <div className={styles.description}>
          <div className={styles.descriptionContent}>
            {patient.language ? (
              <TextWithLineBreaks>{patient.language}</TextWithLineBreaks>
            ) : (
              <NoDescription />
            )}

            {currentlyEditing === editableFieldSchema.enum.language ? (
              <EditContainer>
                <EditLanguage
                  currentLanguage={patient.language}
                  onSuccess={() => setCurrentlyEditing(null)}
                  patientId={patient.id}
                  fieldConfig={fieldConfig}
                />
              </EditContainer>
            ) : null}
          </div>

          <EditControl
            field={editableFieldSchema.enum.language}
            currentlyEditing={currentlyEditing}
            setCurrentlyEditing={setCurrentlyEditing}
          />
        </div>
      </DescriptionList.Description>
    </>
  );
};

const ReasonForAdmission = ({
  currentlyEditing,
  setCurrentlyEditing,
  patient,
  fieldConfig,
}: EditableFieldParameters<"reasonForAdmission"> & {
  fieldConfig: z.infer<typeof informationFieldConfigValueSchema>;
}) => {
  return (
    <>
      <DescriptionList.Term>
        <Trans>Inskrivningsorsak</Trans>
      </DescriptionList.Term>

      <DescriptionList.Description>
        <div className={styles.description}>
          <div className={styles.descriptionContent}>
            {patient.reasonForAdmission ? (
              <TextWithLineBreaks>
                {patient.reasonForAdmission}
              </TextWithLineBreaks>
            ) : (
              <NoDescription />
            )}

            {currentlyEditing ===
            editableFieldSchema.enum.reasonForAdmission ? (
              <EditContainer>
                <EditReasonForAdmission
                  currentReasonForAdmission={patient.reasonForAdmission}
                  onSuccess={() => setCurrentlyEditing(null)}
                  patientId={patient.id}
                  fieldConfig={fieldConfig}
                />
              </EditContainer>
            ) : null}
          </div>

          <EditControl
            field={editableFieldSchema.enum.reasonForAdmission}
            currentlyEditing={currentlyEditing}
            setCurrentlyEditing={setCurrentlyEditing}
          />
        </div>
      </DescriptionList.Description>
    </>
  );
};

const Flags = ({
  currentlyEditing,
  setCurrentlyEditing,
  patient,
  fieldConfig,
}: EditableFieldParameters<"flags"> & {
  fieldConfig: z.infer<typeof informationFieldConfigValueSchema>;
}) => {
  return (
    <>
      <DescriptionList.Term>
        <Trans>Flaggningar</Trans>
      </DescriptionList.Term>

      <DescriptionList.Description>
        <div className={styles.description}>
          <div className={styles.descriptionContent}>
            {patient.flags ? (
              <TextWithLineBreaks>{patient.flags}</TextWithLineBreaks>
            ) : (
              <NoDescription />
            )}

            {currentlyEditing === editableFieldSchema.enum.flags ? (
              <EditContainer>
                <EditFlags
                  currentFlags={patient.flags}
                  onSuccess={() => setCurrentlyEditing(null)}
                  patientId={patient.id}
                  fieldConfig={fieldConfig}
                />
              </EditContainer>
            ) : null}
          </div>

          <EditControl
            field={editableFieldSchema.enum.flags}
            currentlyEditing={currentlyEditing}
            setCurrentlyEditing={setCurrentlyEditing}
          />
        </div>
      </DescriptionList.Description>
    </>
  );
};

const CareSummary = ({
  currentlyEditing,
  setCurrentlyEditing,
  patient,
  fieldConfig,
}: EditableFieldParameters<"careSummary"> & {
  fieldConfig: z.infer<typeof informationFieldConfigValueSchema>;
}) => {
  return (
    <>
      <DescriptionList.Term>
        <Trans>Vårdsammanfattning</Trans>
      </DescriptionList.Term>

      <DescriptionList.Description>
        <div className={styles.description}>
          <div className={styles.descriptionContent}>
            {patient.careSummary ? (
              <TextWithLineBreaks>{patient.careSummary}</TextWithLineBreaks>
            ) : (
              <NoDescription />
            )}

            {currentlyEditing === editableFieldSchema.enum.careSummary ? (
              <EditContainer>
                <EditCareSummary
                  currentCareSummary={patient.careSummary}
                  onSuccess={() => setCurrentlyEditing(null)}
                  patientId={patient.id}
                  fieldConfig={fieldConfig}
                />
              </EditContainer>
            ) : null}
          </div>

          <EditControl
            field={editableFieldSchema.enum.careSummary}
            currentlyEditing={currentlyEditing}
            setCurrentlyEditing={setCurrentlyEditing}
          />
        </div>
      </DescriptionList.Description>
    </>
  );
};

const HomeCare = ({
  currentlyEditing,
  setCurrentlyEditing,
  patient,
}: EditableFieldParameters<"homeCare">) => {
  return (
    <>
      <DescriptionList.Term>
        <Trans>Hemtjänst</Trans>
      </DescriptionList.Term>

      <DescriptionList.Description>
        <div className={styles.description}>
          <div className={styles.descriptionContent}>
            {patient.homeCare ? <Trans>Ja</Trans> : <Trans>Nej</Trans>}

            {currentlyEditing === editableFieldSchema.enum.homeCare ? (
              <EditContainer>
                <EditHomeCare
                  currentHomeCare={patient.homeCare}
                  onSuccess={() => setCurrentlyEditing(null)}
                  patientId={patient.id}
                />
              </EditContainer>
            ) : null}
          </div>

          <EditControl
            field={editableFieldSchema.enum.homeCare}
            currentlyEditing={currentlyEditing}
            setCurrentlyEditing={setCurrentlyEditing}
          />
        </div>
      </DescriptionList.Description>
    </>
  );
};

const LifeCare = ({
  currentlyEditing,
  setCurrentlyEditing,
  patient,
}: EditableFieldParameters<"lifeCare">) => {
  return (
    <>
      <DescriptionList.Term>
        <Trans>LifeCare</Trans>
      </DescriptionList.Term>

      <DescriptionList.Description>
        <div className={styles.description}>
          <div className={styles.descriptionContent}>
            {patient.lifeCare ? <Trans>Ja</Trans> : <Trans>Nej</Trans>}

            {currentlyEditing === editableFieldSchema.enum.lifeCare ? (
              <EditContainer>
                <EditLifeCare
                  currentLifeCare={patient.lifeCare}
                  onSuccess={() => setCurrentlyEditing(null)}
                  patientId={patient.id}
                />
              </EditContainer>
            ) : null}
          </div>

          <EditControl
            field={editableFieldSchema.enum.lifeCare}
            currentlyEditing={currentlyEditing}
            setCurrentlyEditing={setCurrentlyEditing}
          />
        </div>
      </DescriptionList.Description>
    </>
  );
};

const ExternalCareEpisodeId = ({
  currentlyEditing,
  setCurrentlyEditing,
  patient,
  fieldConfig,
}: EditableFieldParameters<"externalCareEpisodeId"> & {
  fieldConfig: z.infer<typeof informationFieldConfigValueSchema>;
}) => {
  return (
    <>
      <DescriptionList.Term>
        <Trans>Externt vårdperiodnummer</Trans>
      </DescriptionList.Term>

      <DescriptionList.Description>
        <div className={styles.description}>
          <div className={styles.descriptionContent}>
            {patient.externalCareEpisodeId ? (
              patient.externalCareEpisodeId
            ) : (
              <NoDescription />
            )}

            {currentlyEditing ===
            editableFieldSchema.enum.externalCareEpisodeId ? (
              <EditContainer>
                <EditExternalCareEpisodeId
                  currentExternalCareEpisodeId={patient.externalCareEpisodeId}
                  onSuccess={() => setCurrentlyEditing(null)}
                  patientId={patient.id}
                  fieldConfig={fieldConfig}
                />
              </EditContainer>
            ) : null}
          </div>

          <EditControl
            field={editableFieldSchema.enum.externalCareEpisodeId}
            currentlyEditing={currentlyEditing}
            setCurrentlyEditing={setCurrentlyEditing}
          />
        </div>
      </DescriptionList.Description>
    </>
  );
};

const PlannedAdmissionDateTime = ({
  currentlyEditing,
  setCurrentlyEditing,
  patient,
  fieldConfig,
}: EditableFieldParameters<"plannedAdmissionDateTime"> & {
  fieldConfig: z.infer<typeof informationFieldConfigValueSchema>;
}) => {
  return (
    <>
      <DescriptionList.Term>
        <Trans>Planerad inskrivning</Trans>
      </DescriptionList.Term>

      <DescriptionList.Description>
        <div className={styles.description}>
          <div className={styles.descriptionContent}>
            {patient.plannedAdmissionDateTime ? (
              dateName(patient.plannedAdmissionDateTime)
            ) : (
              <NoDescription />
            )}

            {currentlyEditing ===
            editableFieldSchema.enum.plannedAdmissionDateTime ? (
              <EditContainer>
                <EditPlannedAdmissionDateTime
                  currentPlannedAdmissionDateTime={
                    patient.plannedAdmissionDateTime
                  }
                  onSuccess={() => setCurrentlyEditing(null)}
                  patientId={patient.id}
                  fieldConfig={fieldConfig}
                />
              </EditContainer>
            ) : null}
          </div>

          <EditControl
            field={editableFieldSchema.enum.plannedAdmissionDateTime}
            currentlyEditing={currentlyEditing}
            setCurrentlyEditing={setCurrentlyEditing}
          />
        </div>
      </DescriptionList.Description>
    </>
  );
};

const PlannedDischargeDateTime = ({
  currentlyEditing,
  setCurrentlyEditing,
  patient,
  fieldConfig,
}: EditableFieldParameters<"plannedDischargeDateTime"> & {
  fieldConfig: z.infer<typeof informationFieldConfigValueSchema>;
}) => {
  return (
    <>
      <DescriptionList.Term>
        <Trans>Planerad utskrivning</Trans>
      </DescriptionList.Term>

      <DescriptionList.Description>
        <div className={styles.description}>
          <div className={styles.descriptionContent}>
            {patient.plannedDischargeDateTime ? (
              dateName(patient.plannedDischargeDateTime)
            ) : (
              <NoDescription />
            )}

            {currentlyEditing ===
            editableFieldSchema.enum.plannedDischargeDateTime ? (
              <EditContainer>
                <EditPlannedDischargeDateTime
                  currentPlannedDischargeDateTime={
                    patient.plannedDischargeDateTime
                  }
                  onSuccess={() => setCurrentlyEditing(null)}
                  patientId={patient.id}
                  fieldConfig={fieldConfig}
                />
              </EditContainer>
            ) : null}
          </div>

          <EditControl
            field={editableFieldSchema.enum.plannedDischargeDateTime}
            currentlyEditing={currentlyEditing}
            setCurrentlyEditing={setCurrentlyEditing}
          />
        </div>
      </DescriptionList.Description>
    </>
  );
};

const PrimaryResponsibleDisplay = ({ employeeId }: { employeeId: number }) => {
  const { data: employee, isPending, isError, error } = useEmployee(employeeId);
  const { t } = useLingui();

  if (isPending) {
    return <Loading message={t`Hämtar medarbetare`} />;
  }

  if (isError) {
    Sentry.captureException(error);
    return (
      <ErrorMessage
        message={`${t`Kunde inte hämta medarbetare`} ${deducedError(error)}`}
      />
    );
  }

  const nameOfEmployee = employeeName(employee, {
    employeeNameFormat: "full",
  });

  return <Text element="p">{nameOfEmployee}</Text>;
};

const PrimaryResponsible = ({
  currentlyEditing,
  setCurrentlyEditing,
  patient,
  fieldConfig,
}: EditableFieldParameters<"primaryResponsible"> & {
  fieldConfig: z.infer<typeof informationFieldConfigValueSchema>;
}) => {
  return (
    <>
      <DescriptionList.Term>
        <Trans>Patientansvarig</Trans>
      </DescriptionList.Term>

      <DescriptionList.Description>
        <div className={styles.description}>
          <div className={styles.descriptionContent}>
            {patient.primaryResponsible ? (
              <PrimaryResponsibleDisplay
                employeeId={patient.primaryResponsible}
              />
            ) : (
              <NoDescription />
            )}

            {currentlyEditing ===
            editableFieldSchema.enum.primaryResponsible ? (
              <EditContainer>
                <EditPrimaryResponsible
                  currentPrimaryResponsible={patient.primaryResponsible}
                  onSuccess={() => setCurrentlyEditing(null)}
                  patientId={patient.id}
                  fieldConfig={fieldConfig}
                />
              </EditContainer>
            ) : null}
          </div>

          <EditControl
            field={editableFieldSchema.enum.primaryResponsible}
            currentlyEditing={currentlyEditing}
            setCurrentlyEditing={setCurrentlyEditing}
          />
        </div>
      </DescriptionList.Description>
    </>
  );
};

const ReferringUnit = ({
  currentlyEditing,
  setCurrentlyEditing,
  patient,
  fieldConfig,
}: EditableFieldParameters<"referringUnit"> & {
  fieldConfig: z.infer<typeof informationFieldConfigValueSchema>;
}) => {
  return (
    <>
      <DescriptionList.Term>
        <Trans>Remitterande enhet</Trans>
      </DescriptionList.Term>

      <DescriptionList.Description>
        <div className={styles.description}>
          <div className={styles.descriptionContent}>
            {patient.referringUnit ? patient.referringUnit : <NoDescription />}

            {currentlyEditing === editableFieldSchema.enum.referringUnit ? (
              <EditContainer>
                <EditReferringUnit
                  currentReferringUnit={patient.referringUnit}
                  onSuccess={() => setCurrentlyEditing(null)}
                  patientId={patient.id}
                  fieldConfig={fieldConfig}
                />
              </EditContainer>
            ) : null}
          </div>

          <EditControl
            field={editableFieldSchema.enum.referringUnit}
            currentlyEditing={currentlyEditing}
            setCurrentlyEditing={setCurrentlyEditing}
          />
        </div>
      </DescriptionList.Description>
    </>
  );
};

const PlaceOfAssessment = ({
  currentlyEditing,
  setCurrentlyEditing,
  patient,
  fieldConfig,
}: EditableFieldParameters<"placeOfAssessment"> & {
  fieldConfig: z.infer<typeof informationFieldConfigValueSchema>;
}) => {
  return (
    <>
      <DescriptionList.Term>
        <Trans>Plats för bedömning</Trans>
      </DescriptionList.Term>

      <DescriptionList.Description>
        <div className={styles.description}>
          <div className={styles.descriptionContent}>
            {patient.placeOfAssessment ? (
              patient.placeOfAssessment
            ) : (
              <NoDescription />
            )}

            {currentlyEditing === editableFieldSchema.enum.placeOfAssessment ? (
              <EditContainer>
                <EditPlaceOfAssessment
                  currentPlaceOfAssessment={patient.placeOfAssessment}
                  onSuccess={() => setCurrentlyEditing(null)}
                  patientId={patient.id}
                  fieldConfig={fieldConfig}
                />
              </EditContainer>
            ) : null}
          </div>

          <EditControl
            field={editableFieldSchema.enum.placeOfAssessment}
            currentlyEditing={currentlyEditing}
            setCurrentlyEditing={setCurrentlyEditing}
          />
        </div>
      </DescriptionList.Description>
    </>
  );
};

const PaymentExempt = ({
  currentlyEditing,
  setCurrentlyEditing,
  patient,
}: EditableFieldParameters<"paymentExempt">) => {
  return (
    <>
      <DescriptionList.Term>
        <Trans>Betalningsbefriad</Trans>
      </DescriptionList.Term>

      <DescriptionList.Description>
        <div className={styles.description}>
          <div className={styles.descriptionContent}>
            {patient.paymentExempt ? <Trans>Ja</Trans> : <Trans>Nej</Trans>}

            {currentlyEditing === editableFieldSchema.enum.paymentExempt ? (
              <EditContainer>
                <EditPaymentExempt
                  currentPaymentExempt={patient.paymentExempt}
                  onSuccess={() => setCurrentlyEditing(null)}
                  patientId={patient.id}
                />
              </EditContainer>
            ) : null}
          </div>

          <EditControl
            field={editableFieldSchema.enum.paymentExempt}
            currentlyEditing={currentlyEditing}
            setCurrentlyEditing={setCurrentlyEditing}
          />
        </div>
      </DescriptionList.Description>
    </>
  );
};

const PostDischargeProcessed = ({
  currentlyEditing,
  setCurrentlyEditing,
  patient,
}: EditableFieldParameters<"postDischargeProcessed">) => {
  return (
    <>
      <DescriptionList.Term>
        <Trans>Efterhanterad</Trans>
      </DescriptionList.Term>

      <DescriptionList.Description>
        <div className={styles.description}>
          <div className={styles.descriptionContent}>
            {patient.postDischargeProcessed ? (
              <Trans>Ja</Trans>
            ) : (
              <Trans>Nej</Trans>
            )}

            {currentlyEditing ===
            editableFieldSchema.enum.postDischargeProcessed ? (
              <EditContainer>
                <EditPostDischargeProcessed
                  currentPostDischargeProcessed={patient.postDischargeProcessed}
                  onSuccess={() => setCurrentlyEditing(null)}
                  patientId={patient.id}
                />
              </EditContainer>
            ) : null}
          </div>

          <EditControl
            field={editableFieldSchema.enum.postDischargeProcessed}
            currentlyEditing={currentlyEditing}
            setCurrentlyEditing={setCurrentlyEditing}
          />
        </div>
      </DescriptionList.Description>
    </>
  );
};

const PatientType = ({ patientType }: { patientType: IPatientType }) => {
  const { t } = useLingui();
  return (
    <>
      <DescriptionList.Term>
        <Trans>Patienttyp</Trans>
      </DescriptionList.Term>

      <DescriptionList.Description>
        <div className={styles.description}>
          {t(patientTypeDictionary[patientType])}
        </div>
      </DescriptionList.Description>
    </>
  );
};

const DateOf = ({ patient }: { patient: IExistingPatient }) => {
  const { status } = patient;

  if (status === patientStatusSchema.enum.prospect && patient.registeredAt) {
    return (
      <>
        <DescriptionList.Term>
          <Trans>Registreringsdatum</Trans>
        </DescriptionList.Term>

        <DescriptionList.Description>
          <div className={styles.description}>
            {dateName(patient.registeredAt)}
          </div>
        </DescriptionList.Description>
      </>
    );
  }

  if (status === patientStatusSchema.enum.admitted && patient.admittedAt) {
    return (
      <>
        <DescriptionList.Term>
          <Trans>Inskrivningsdatum</Trans>
        </DescriptionList.Term>

        <DescriptionList.Description>
          <div className={styles.description}>
            {dateName(patient.admittedAt)}
          </div>
        </DescriptionList.Description>
      </>
    );
  }

  if (status === patientStatusSchema.enum.discharged) {
    return (
      <>
        {patient.admittedAt ? (
          <>
            <DescriptionList.Term>
              <Trans>Inskrivningsdatum</Trans>
            </DescriptionList.Term>

            <DescriptionList.Description>
              <div className={styles.description}>
                {dateName(patient.admittedAt)}
              </div>
            </DescriptionList.Description>
          </>
        ) : null}
        {patient.dischargedAt ? (
          <>
            <DescriptionList.Term>
              <Trans>Utskrivningsdatum</Trans>
            </DescriptionList.Term>
            <DescriptionList.Description>
              <div className={styles.description}>
                {dateName(patient.dischargedAt)}
              </div>
            </DescriptionList.Description>
          </>
        ) : null}
      </>
    );
  }

  return null;
};

const Information = () => {
  const { t } = useLingui();

  const { data: patientTypes } = usePatientTypes();
  const { data: teams } = useTeams();
  const { data: economicUnits } = useEconomicUnits();
  const { patientId } = z.object({ patientId: z.string() }).parse(useParams());
  const [currentlyEditing, setCurrentlyEditing] =
    useState<EditableField | null>(null);
  const { data: patient, isPending, isError, error } = usePatient(patientId);
  const { data: cardiologyEnabled } = useFeatureFlag(
    knownFeatureFlagsSchema.enum.CardiologyWorkflow,
  );
  const { data: areaEnabled } = useFeatureFlag(
    knownFeatureFlagsSchema.enum.Area,
  );

  const {
    data: conditionalInfoFields,
    isPending: isPendingConditionalInfoFields,
    isError: isErrorConditionalInfoFields,
    error: errorConditionalInfoFields,
  } = usePatientInformationFields();

  if (isPending || isPendingConditionalInfoFields) {
    return <Loading message={t`Laddar patientinformation`} padding={24} />;
  }
  if (isError) {
    Sentry.captureException(error);
    return <ErrorMessage message={deducedError(error)} padding={24} />;
  }
  if (isErrorConditionalInfoFields) {
    Sentry.captureException(errorConditionalInfoFields);
    return (
      <ErrorMessage
        message={deducedError(errorConditionalInfoFields)}
        padding={24}
      />
    );
  }

  const shouldShowField = (fieldName: keyof typeof conditionalInfoFields) =>
    isPatientInformationFieldVisible({
      fieldName,
      configValue: conditionalInfoFields[fieldName],
      patientStatus: patient.status,
    });

  return (
    <div className={styles.information}>
      <div className={styles.details}>
        <DescriptionList>
          <DateOf patient={patient} />

          {shouldShowField("PlannedAdmissionDateTime") ? (
            <PlannedAdmissionDateTime
              currentlyEditing={currentlyEditing}
              setCurrentlyEditing={setCurrentlyEditing}
              patient={patient}
              fieldConfig={conditionalInfoFields.PlannedAdmissionDateTime}
            />
          ) : null}

          {shouldShowField("PlannedDischargeDateTime") ? (
            <PlannedDischargeDateTime
              currentlyEditing={currentlyEditing}
              setCurrentlyEditing={setCurrentlyEditing}
              patient={patient}
              fieldConfig={conditionalInfoFields.PlannedDischargeDateTime}
            />
          ) : null}

          {patientTypes && patientTypes.length > 1 ? (
            <PatientType patientType={patient.patientType} />
          ) : null}

          <Name
            currentlyEditing={currentlyEditing}
            setCurrentlyEditing={setCurrentlyEditing}
            patient={patient}
          />

          {shouldShowField("ExternalCareEpisodeId") ? (
            <ExternalCareEpisodeId
              currentlyEditing={currentlyEditing}
              setCurrentlyEditing={setCurrentlyEditing}
              patient={patient}
              fieldConfig={conditionalInfoFields.ExternalCareEpisodeId}
            />
          ) : null}

          {shouldShowField("DiagnosisCode") ? (
            <DiagnosisCode
              currentlyEditing={currentlyEditing}
              setCurrentlyEditing={setCurrentlyEditing}
              patient={patient}
              fieldConfig={conditionalInfoFields.DiagnosisCode}
            />
          ) : null}

          <Address
            currentlyEditing={currentlyEditing}
            setCurrentlyEditing={setCurrentlyEditing}
            patient={patient}
          />

          {shouldShowField("AdditionalAdressInformation") ? (
            <AdditionalAddressInformation
              currentlyEditing={currentlyEditing}
              setCurrentlyEditing={setCurrentlyEditing}
              patient={patient}
              fieldConfig={conditionalInfoFields.AdditionalAdressInformation}
            />
          ) : null}

          {shouldShowField("KeyToPatient") ? (
            <Key
              currentlyEditing={currentlyEditing}
              setCurrentlyEditing={setCurrentlyEditing}
              patient={patient}
              fieldConfig={conditionalInfoFields.KeyToPatient}
            />
          ) : null}

          {areaEnabled ? (
            <GeographicalArea
              currentlyEditing={currentlyEditing}
              setCurrentlyEditing={setCurrentlyEditing}
              patient={patient}
            />
          ) : null}

          {teams && teams.length > 0 ? (
            <Team
              currentlyEditing={currentlyEditing}
              setCurrentlyEditing={setCurrentlyEditing}
              patient={patient}
            />
          ) : null}

          {economicUnits && economicUnits.length > 1 ? (
            <EconomicUnit
              currentlyEditing={currentlyEditing}
              setCurrentlyEditing={setCurrentlyEditing}
              patient={patient}
            />
          ) : null}

          <Phone
            currentlyEditing={currentlyEditing}
            setCurrentlyEditing={setCurrentlyEditing}
            patient={patient}
          />

          {shouldShowField("GeneralInformation") ? (
            <GeneralInformation
              currentlyEditing={currentlyEditing}
              setCurrentlyEditing={setCurrentlyEditing}
              patient={patient}
              fieldConfig={conditionalInfoFields.GeneralInformation}
            />
          ) : null}

          {shouldShowField("CareSummary") ? (
            <CareSummary
              currentlyEditing={currentlyEditing}
              setCurrentlyEditing={setCurrentlyEditing}
              patient={patient}
              fieldConfig={conditionalInfoFields.CareSummary}
            />
          ) : null}

          {shouldShowField("ReasonForAdmission") ? (
            <ReasonForAdmission
              currentlyEditing={currentlyEditing}
              setCurrentlyEditing={setCurrentlyEditing}
              patient={patient}
              fieldConfig={conditionalInfoFields.ReasonForAdmission}
            />
          ) : null}

          {cardiologyEnabled ? (
            <Ward
              currentlyEditing={currentlyEditing}
              setCurrentlyEditing={setCurrentlyEditing}
              patient={patient}
            />
          ) : null}

          {shouldShowField("CloseContact") ? (
            <Relatives
              currentlyEditing={currentlyEditing}
              setCurrentlyEditing={setCurrentlyEditing}
              patient={patient}
            />
          ) : null}

          {shouldShowField("PrimaryResponsible") ? (
            <PrimaryResponsible
              currentlyEditing={currentlyEditing}
              setCurrentlyEditing={setCurrentlyEditing}
              patient={patient}
              fieldConfig={conditionalInfoFields.PrimaryResponsible}
            />
          ) : null}

          {shouldShowField("ReferringUnit") ? (
            <ReferringUnit
              currentlyEditing={currentlyEditing}
              setCurrentlyEditing={setCurrentlyEditing}
              patient={patient}
              fieldConfig={conditionalInfoFields.ReferringUnit}
            />
          ) : null}

          {shouldShowField("PlaceOfAssessment") ? (
            <PlaceOfAssessment
              currentlyEditing={currentlyEditing}
              setCurrentlyEditing={setCurrentlyEditing}
              patient={patient}
              fieldConfig={conditionalInfoFields.PlaceOfAssessment}
            />
          ) : null}

          {shouldShowField("PaymentExempt") ? (
            <PaymentExempt
              currentlyEditing={currentlyEditing}
              setCurrentlyEditing={setCurrentlyEditing}
              patient={patient}
            />
          ) : null}

          {shouldShowField("PostDischargeProcessed") ? (
            <PostDischargeProcessed
              currentlyEditing={currentlyEditing}
              setCurrentlyEditing={setCurrentlyEditing}
              patient={patient}
            />
          ) : null}

          {shouldShowField("MedicalTransportNumber") ? (
            <MedicalTransportNumber
              currentlyEditing={currentlyEditing}
              setCurrentlyEditing={setCurrentlyEditing}
              patient={patient}
              fieldConfig={conditionalInfoFields.MedicalTransportNumber}
            />
          ) : null}

          {shouldShowField("PersWristband") ? (
            <SafetyAlarm
              currentlyEditing={currentlyEditing}
              setCurrentlyEditing={setCurrentlyEditing}
              patient={patient}
              fieldConfig={conditionalInfoFields.PersWristband}
            />
          ) : null}

          {shouldShowField("Pet") ? (
            <Pet
              currentlyEditing={currentlyEditing}
              setCurrentlyEditing={setCurrentlyEditing}
              patient={patient}
              fieldConfig={conditionalInfoFields.Pet}
            />
          ) : null}

          {shouldShowField("Flags") ? (
            <Flags
              currentlyEditing={currentlyEditing}
              setCurrentlyEditing={setCurrentlyEditing}
              patient={patient}
              fieldConfig={conditionalInfoFields.Flags}
            />
          ) : null}

          {shouldShowField("Language") ? (
            <Language
              currentlyEditing={currentlyEditing}
              setCurrentlyEditing={setCurrentlyEditing}
              patient={patient}
              fieldConfig={conditionalInfoFields.Language}
            />
          ) : null}

          {shouldShowField("HomeCare") ? (
            <HomeCare
              currentlyEditing={currentlyEditing}
              setCurrentlyEditing={setCurrentlyEditing}
              patient={patient}
            />
          ) : null}

          {shouldShowField("LifeCare") ? (
            <LifeCare
              currentlyEditing={currentlyEditing}
              setCurrentlyEditing={setCurrentlyEditing}
              patient={patient}
            />
          ) : null}
        </DescriptionList>
      </div>
      <div className={styles.map}>
        <PatientMap
          coordinates={patient.address.coordinates}
          style={{ height: "100%" }}
          allowScrollZoom
        />
      </div>
    </div>
  );
};

export default Information;
